import React from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import PlannedChangesReview, { ACTION_LABELS } from './PlannedChangesReview';

const CHECK_LABELS = {
  payload_checksum: 'Package checksum',
  package_signature: 'Package signature',
  source_environment: 'Source environment allowed',
  package_schema_version: 'Package format version',
  application_version: 'Application version parity',
  migration_state: 'Database migration parity',
  sponsor: 'Sponsor exists in this environment',
  entity_counts: 'Package contents match its manifest',
  devices: 'Referenced devices exist',
  screen_templates: 'Referenced screen templates exist',
  vpg_library_questions: 'Referenced VPG library questions exist',
  collision: 'Study collision check',
};

const CLASSIFICATION_BADGES = {
  new_study: { label: 'New study', classes: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  re_promotion: { label: 'Re-promotion', classes: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  conflict: { label: 'Conflict', classes: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
};

const PreflightReportView = ({ manifest, target, report, payload }) => {
  if (!report) return null;
  const badge = CLASSIFICATION_BADGES[report.classification] || null;

  return (
    <div className="space-y-6">
      {/* Candidate summary */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Candidate {manifest?.candidate_label}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-mono break-all">
              {manifest?.candidate_id}
            </p>
          </div>
          {badge && (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.classes}`}>
              {badge.label}
            </span>
          )}
        </div>
        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Study</dt>
            <dd className="text-gray-900 dark:text-gray-100">
              {manifest?.study?.title} ({manifest?.study?.study_code})
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Sponsor</dt>
            <dd className="text-gray-900 dark:text-gray-100">{manifest?.sponsor?.sponsor_name}</dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">From</dt>
            <dd className="text-gray-900 dark:text-gray-100">
              {manifest?.source_environment} @ {manifest?.application_version}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Exported by</dt>
            <dd className="text-gray-900 dark:text-gray-100">
              {manifest?.exported_by || 'unattributed'}
              {manifest?.exported_at ? `, ${new Date(manifest.exported_at).toLocaleString()}` : ''}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Into</dt>
            <dd className="text-gray-900 dark:text-gray-100 font-semibold">
              {target?.environment} @ {target?.application_version}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">This environment accepts</dt>
            <dd className="text-gray-900 dark:text-gray-100">
              {target?.allowed_source_environments?.length
                ? `builds from ${target.allowed_source_environments.join(', ')}`
                : 'builds from any environment'}
              {target?.signing_enforced ? ', signed only' : ''}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-gray-500 dark:text-gray-400">Checksum</dt>
            <dd className="text-gray-900 dark:text-gray-100 font-mono text-xs break-all">
              {manifest?.payload_checksum}
            </dd>
          </div>
        </dl>
        {manifest?.entity_counts && (
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.entries(manifest.entity_counts).map(([entity, count]) => (
              <span
                key={entity}
                className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300"
              >
                {entity.replace(/_/g, ' ')}: {count}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Checks */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Preflight checks
        </h3>
        <ul className="space-y-3">
          {report.checks?.map((check) => (
            <li key={check.name} className="flex items-start gap-3">
              {check.passed ? (
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
              )}
              <div>
                <p className={`text-sm font-medium ${check.passed ? 'text-gray-900 dark:text-gray-100' : 'text-red-700 dark:text-red-400'}`}>
                  {CHECK_LABELS[check.name] || check.name}
                </p>
                {check.detail && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{check.detail}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Planned changes — deep review when the parsed package is available,
          otherwise the compact per-entity action table */}
      {payload ? (
        <PlannedChangesReview
          payload={payload}
          plannedActions={report.planned_actions}
          classification={report.classification}
        />
      ) : report.planned_actions && Object.keys(report.planned_actions).length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Planned actions
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-6 font-medium">Content</th>
                  <th className="py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {Object.entries(report.planned_actions).map(([entity, action]) => (
                  <tr key={entity}>
                    <td className="py-2 pr-6 text-gray-900 dark:text-gray-100">
                      {entity.replace(/_/g, ' ')}
                    </td>
                    <td className="py-2 text-gray-700 dark:text-gray-300">
                      {ACTION_LABELS[action] || action}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreflightReportView;
